import { SortableTh } from "../components/SortableTh";
import { useSortableRows } from "../hooks/useSortableRows";
import type { ExpensiveGeneration } from "../types/api";

interface TopExpensiveTableProps {
  rows: ExpensiveGeneration[];
  /** Context-length p95 — rows above it get a "long context" badge. */
  contextP95: number;
}

const TD = "px-6 py-4 text-sm text-slate-900";

type ExpensiveSortKey =
  | "userId"
  | "modelId"
  | "estimatedCostUsd"
  | "contextLength"
  | "inputLength"
  | "tokensIn"
  | "createdAt";
const ACCESSORS: Record<ExpensiveSortKey, (r: ExpensiveGeneration) => string | number | null> = {
  userId: (r) => r.userId,
  modelId: (r) => r.modelId,
  estimatedCostUsd: (r) => r.estimatedCostUsd,
  contextLength: (r) => r.contextLength,
  inputLength: (r) => r.inputLength,
  tokensIn: (r) => r.tokensIn,
  createdAt: (r) => r.createdAt,
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function TopExpensiveTable({ rows, contextP95 }: TopExpensiveTableProps) {
  // Default: costliest generation first — the thing this table exists to surface.
  const { sorted, sort, toggleSort } = useSortableRows(rows, ACCESSORS, {
    key: "estimatedCostUsd",
    direction: "desc",
  });

  if (rows.length === 0) {
    return <div className="text-slate-500 text-sm">No generations in window.</div>;
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortableTh label="User" sortKey="userId" sort={sort} onSort={toggleSort} sticky />
              <SortableTh label="Model" sortKey="modelId" sort={sort} onSort={toggleSort} />
              <SortableTh
                label="Cost (USD)"
                sortKey="estimatedCostUsd"
                sort={sort}
                onSort={toggleSort}
              />
              <SortableTh
                label="Context Length"
                sortKey="contextLength"
                sort={sort}
                onSort={toggleSort}
              />
              <SortableTh
                label="Input Length"
                sortKey="inputLength"
                sort={sort}
                onSort={toggleSort}
              />
              <SortableTh label="Tokens In" sortKey="tokensIn" sort={sort} onSort={toggleSort} />
              <SortableTh label="Created" sortKey="createdAt" sort={sort} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {sorted.map((r) => {
              const longContext = contextP95 > 0 && r.contextLength > contextP95;
              return (
                <tr key={r.id} className="group hover:bg-slate-50 transition-colors">
                  <td
                    className={`${TD} sticky left-0 z-10 bg-white group-hover:bg-slate-50 font-mono text-xs`}
                  >
                    {r.userId}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{r.modelId}</td>
                  <td className={`${TD} font-medium`}>${r.estimatedCostUsd.toFixed(4)}</td>
                  <td className={TD}>
                    {r.contextLength.toLocaleString()}
                    {longContext && (
                      <span className="ml-2 inline-block rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        long context
                      </span>
                    )}
                  </td>
                  <td className={TD}>{r.inputLength.toLocaleString()}</td>
                  <td className={TD}>{r.tokensIn.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{formatDate(r.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
